// public/js/utils/table-sort.js
// Reusable click-to-sort for plain tables. Columns map positionally to the
// table's <th> cells. Supports 'string' | 'number' | 'date' types, shows an
// asc/desc indicator on the active column, and always pushes empty values last.
//
// Usage:
//   const sorter = createTableSorter(columns, renderTable);
//   sorter.decorate(theadRowEl);          // once, after the table HTML exists
//   const rows = sorter.sort(filteredRows); // inside renderTable
//
// columns: [{ label?, type, accessor, sortable }] — one entry per <th>, in order.

function isEmpty(v) { return v == null || v === ''; }

function cmp(va, vb, type) {
  if (type === 'number') return Number(va) - Number(vb);
  if (type === 'date') return new Date(va).getTime() - new Date(vb).getTime();
  // Natural string compare (so "tracker2" < "tracker10").
  return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
}

export function createTableSorter(columns, onChange) {
  let sortIdx = null; // active column index
  let dir = 1;        // 1 = asc, -1 = desc

  function sort(rows) {
    if (sortIdx == null) return rows;
    const col = columns[sortIdx];
    if (!col || !col.sortable) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const va = col.accessor(a);
      const vb = col.accessor(b);
      const ma = isEmpty(va), mb = isEmpty(vb);
      if (ma && mb) return 0;
      if (ma) return 1;   // empties always last…
      if (mb) return -1;  // …regardless of direction
      const c = cmp(va, vb, col.type);
      if (c !== 0) return dir * c;
      return 0;
    });
    return copy;
  }

  function updateIndicators(theadRow) {
    const ths = theadRow.querySelectorAll('th');
    columns.forEach((col, i) => {
      const ind = ths[i] && ths[i].querySelector('.sort-ind');
      if (!ind) return;
      if (sortIdx === i) {
        ind.className = `fa-solid ${dir === 1 ? 'fa-sort-up' : 'fa-sort-down'} sort-ind text-primary ms-1`;
        ind.style.opacity = '1';
      } else {
        ind.className = 'fa-solid fa-sort sort-ind text-muted ms-1';
        ind.style.opacity = '0.4';
      }
    });
  }

  function decorate(theadRow) {
    if (!theadRow) return;
    const ths = theadRow.querySelectorAll('th');
    columns.forEach((col, i) => {
      const th = ths[i];
      if (!th || !col.sortable) return;
      th.style.cursor = 'pointer';
      th.style.userSelect = 'none';
      th.title = 'Sort';
      if (!th.querySelector('.sort-ind')) {
        th.insertAdjacentHTML('beforeend', ` <i class="fa-solid fa-sort sort-ind text-muted ms-1" style="font-size:0.8em;opacity:0.4;"></i>`);
      }
      th.addEventListener('click', () => {
        if (sortIdx === i) dir = -dir;
        else { sortIdx = i; dir = 1; }
        updateIndicators(theadRow);
        onChange();
      });
    });
    updateIndicators(theadRow);
  }

  return { sort, decorate, getState: () => ({ sortIdx, dir }) };
}
